import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaWhatsapp, FaClock } from 'react-icons/fa';
import { companyInfo } from '../data/tourData';
import './WriteToUs.css';

const WriteToUs = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    preferredDestination: '',
    message: '',
    subscribeNewsletter: false
  });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    
    // Auto-hide message after 5 seconds
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.subject || !formData.message) {
      showMessage('Please fill in all required fields.', 'error');
      return;
    }

    // Show loading state
    showMessage('Sending message...', 'info');

    try {
      // Use Web3Forms (Free service - get your access key from https://web3forms.com)
      const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_KEY || 'YOUR_WEB3FORMS_ACCESS_KEY';
      
      const contactData = {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: `Contact Form: ${formData.subject}`,
        from_name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        inquiry_subject: formData.subject,
        preferred_destination: formData.preferredDestination || 'Not specified',
        message: formData.message,
        newsletter_subscription: formData.subscribeNewsletter ? 'Yes' : 'No',
        timestamp: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
      };

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(contactData)
      });

      const result = await response.json();

      if (result.success) {
        showMessage('Message sent successfully! We\'ll get back to you soon.', 'success');
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          subject: '',
          preferredDestination: '',
          message: '',
          subscribeNewsletter: false
        });
      } else {
        showMessage('Submission failed. Please try again or contact us directly.', 'error');
      }
      
    } catch (error) {
      console.error('Submission error:', error);
      showMessage('Network error. Please try again or contact us directly.', 'error');
    }
  };

  return (
    <>
      <Helmet>
        <title>Write to Us - NomadTrailz</title>
        <meta name="description" content="Get in touch with NomadTrailz. We're here to help you plan your perfect adventure." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <section className="contact-hero">
          <div className="hero-background">
            <img 
              src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
              alt="Mountain Landscape" 
            />
            <div className="hero-overlay"></div>
          </div>
          {/* Floating Particles */}
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="hero-content">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Write to Us
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              We're here to help you plan your perfect adventure
            </motion.p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="contact-section">
          <div className="floating-element"></div>
          <div className="floating-element-2"></div>
          <div className="floating-element-3"></div>
          <div className="floating-element-4"></div>
          <div className="floating-element-5"></div>
          <div className="mountain-silhouette"></div>
          <div className="container">
            <div className="contact-grid">
              <div className="contact-info">
                <motion.div 
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Get in Touch</h2>
                  <p>
                    Have questions about our tours? Want to customize your adventure? 
                    We'd love to hear from you! Our team is here to help you plan 
                    the perfect journey.
                  </p>
                </motion.div>

                <motion.div 
                  className="contact-details"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div className="contact-item">
                    <FaPhone />
                    <div>
                      <h4>Phone</h4>
                      <p>{companyInfo.phone}</p>
                    </div>
                  </div>
                  <div className="contact-item">
                    <FaEnvelope />
                    <div>
                      <h4>Email</h4>
                      <p>{companyInfo.email}</p>
                    </div>
                  </div>
                  <div className="contact-item">
                    <FaMapMarkerAlt />
                    <div>
                      <h4>Address</h4>
                      <p>{companyInfo.address}</p>
                    </div>
                  </div>
                  <div className="contact-item">
                    <FaClock />
                    <div>
                      <h4>Business Hours</h4>
                      <p>Monday - Sunday: 9:00 AM - 8:00 PM</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div 
                  className="whatsapp-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Quick Contact via WhatsApp</h3>
                  <p>For instant responses and quick queries, reach out to us on WhatsApp.</p>
                  <a 
                    href={companyInfo.social.whatsapp} 
                    className="whatsapp-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaWhatsapp />
                    Chat on WhatsApp
                  </a>
                </motion.div>
              </div>

              <div className="contact-form">
                <motion.form 
                  className="form-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  onSubmit={handleSubmit}
                >
                  <h3>Send us a Message</h3>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name *</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="lastName">Last Name *</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="subject">Subject *</label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select a subject</option>
                      <option value="general-inquiry">General Inquiry</option>
                      <option value="booking-information">Booking Information</option>
                      <option value="custom-tour-request">Custom Tour Request</option>
                      <option value="customer-support">Customer Support</option>
                      <option value="feedback">Feedback</option>
                      <option value="partnership">Partnership</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="preferredDestination">Preferred Destination</label>
                    <select
                      id="preferredDestination"
                      name="preferredDestination"
                      value={formData.preferredDestination}
                      onChange={handleChange}
                    >
                      <option value="">Select destination (optional)</option>
                      <option value="spiti-valley">Spiti Valley</option>
                      <option value="meghalaya">Meghalaya</option>
                      <option value="kashmir">Kashmir</option>
                      <option value="kerala">Kerala</option>
                      <option value="leh-ladakh">Leh Ladakh</option>
                      <option value="trekking-tours">Trekking Tours</option>
                      <option value="panch-kailash">Panch Kailash</option>
                      <option value="panch-kedar">Panch Kedar</option>
                      <option value="custom-destination">Custom Destination</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Please describe your inquiry or requirements..."
                      required
                    ></textarea>
                  </div>

                  <div className="form-group checkbox-group">
                    <input
                      type="checkbox"
                      id="subscribeNewsletter"
                      name="subscribeNewsletter"
                      checked={formData.subscribeNewsletter}
                      onChange={handleChange}
                    />
                    <label htmlFor="subscribeNewsletter">
                      Subscribe to our newsletter for travel updates and offers
                    </label>
                  </div>

                  <button type="submit" className="submit-btn">
                    Send Message
                  </button>
                </motion.form>

                {message && (
                  <div className={`contact-message ${messageType}`}>
                    {message}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default WriteToUs;
